import EventClass from 'lib/EventClass';
import evaluateFn from 'lib/evaluateFn';
import Configurator from 'lib/Configurator';
import slotUtilities from 'lib/slotUtilities';
import getBreakpointSize from 'lib/getBreakpointSize';
import uniqueId from 'lib/uniqueId';
import consolv from 'lib/consolv';
import { prepareGPT } from 'lib/loadGPTLibrary';
import buildSizeMap from 'lib/buildSizeMap';
import filterSizes from 'lib/filterSizes';
import buildResponsiveSizeMap from 'lib/buildResponsiveSizeMap';
import getPageTargeting from './getPageTargeting';

import DFPAdSlot from './DFPAdSlot';
import DFPOutOfPageAdSlot from './DFPOutOfPageAdSlot';

/**
 * This is the primary ad library class. Most interaction with the ad library will be done through
 * the class properties.
 *
 * This class is automatically instantiated and is accessible via the window.DatMCP object.
 */
class MCP extends EventClass {
  _sourceOfTruth;

  _isLoaded = false;

  _isReady = false;

  _hasError = false;

  _additionalTargeting = {};

  _rules = [];

  _config;

  _hierarchyFn = () => {};

  _pageTargetingFn = () => {};

  _params;

  _environmentOverride;

  _breakpoints;

  _overlaySelectors;

   utilities = {};

  /**
   * @private
   */
  Configurator;

  /**
   * A Map object of slots indexed by the Node object of the slot container.
   */
  slots;

  /**
   * An object of out of page slots indexed by a unique identifier
   */
  outOfPageSlots = {};

  ready;

  loaded;

  _resolveReady;

  _rejectReady;

  /**
   * @private
   */
  constructor({ sourceOfTruth, rules, hierarchyFn, selectedNetwork, ...config }) {
    super();

    /* eslint-disable no-underscore-dangle */
    this._setupDependencies = this._setupDependencies.bind(this);
    this._applyGPTSettings = this._applyGPTSettings.bind(this);
    this._enableGPTServices = this._enableGPTServices.bind(this);
    this._applyPageTargeting = this._applyPageTargeting.bind(this);
    this._resolveSlotReference = this._resolveSlotReference.bind(this);
    this._defineAdSlot = this._defineAdSlot.bind(this);
    this._defineOutOfPageAdSlot = this._defineOutOfPageAdSlot.bind(this);
    this._handleSlotVisibilityChanged = this._handleSlotVisibilityChanged.bind(this);
    this._handleImpressionViewable = this._handleImpressionViewable.bind(this);
    this._handleSlotRenderEnded = this._handleSlotRenderEnded.bind(this);
    this._updateSlotMeta = this._updateSlotMeta.bind(this);
    this.getSlot = this.getSlot.bind(this);
    this.appendSlot = this.appendSlot.bind(this);
    this.refreshSlot = this.refreshSlot.bind(this);
    this.refreshSlots = this.refreshSlots.bind(this);
    this.pruneOrphanSlots = this.pruneOrphanSlots.bind(this);
    this.getHierarchy = this.getHierarchy.bind(this);
    this.getPageTargeting = this.getPageTargeting.bind(this);
    this.initialize = this.initialize.bind(this);
    /* eslint-enable no-underscore-dangle */

    this._config = config;
    this._hierarchyFn = hierarchyFn;
    this.utilities = { ...this.utilities, ...slotUtilities };
    this.slots = new Map();

    this.ready = new Promise((resolve, reject) => {
      this._resolveReady = resolve;
      this._rejectReady = reject;
    });

    this.loaded = this._setupDependencies({
      sourceOfTruth,
      rules,
    });
  }

  /**
   * Set to true once the ad library is ready to accept commands.
   * @type {boolean}
   */
  get isLoaded() {
    return this._isLoaded;
  }

  /**
   * Set to true once the ad library is ready to accept commands.
   * @type {boolean}
   * @private
   */
  get isReady() {
    return this._isReady;
  }

  get slotsById() {
    return Array.from(this.slots.values()).reduce((p, slot) => {
      if (slot.enabled !== false) p[slot.id] = slot;
      return p;
    }, {});
  }

  /**
   * Set to true when an unhandled error occurs in the ad library.
   * @return {boolean}
   */
  get hasError() {
    return this._hasError;
  }

  getOption(optionKey) {
    const config = this.Configurator.getConfiguration();
    return config.options && config.options[optionKey];
  }

  /**
   * Waits for async functions to complete before finishing the initialization process
   * @private
   */
  _setupDependencies({ rules, sourceOfTruth }) {
    return Promise.all([sourceOfTruth, rules]).then(([sourceOfTruthRes, rulesRes]) => {
      // Sets up googletag global if it isn't already there
      prepareGPT();

      this._sourceOfTruth = sourceOfTruthRes;
      this._rules = rulesRes;

      // Creates a function that will pull the meta data from the SourthOfTruth
      const getMeta = () => this._sourceOfTruth.meta;

      this.Configurator = new Configurator(this._rules, getMeta);

      const { dfpEnvironment } = this.Configurator.getConfiguration();
      this._selectedNetwork = dfpEnvironment;

      this._isLoaded = true;
      this.dispatchEvent('loaded');

      return this;
    })
      .catch((e) => {
        consolv.error(e);
        throw new Error('MCP002 Dependency Error');
      });
  }

  /**
   * @private
   */
  _applyGPTSettings() {
    const { googletag } = window;
    const {
      collapseEmptyDivs = true,
      singleRequestMode = true,
      centerAds = true,
      disableInitialLoad = true,
      restrictDataProcessing = false,
    } = this.Configurator.getConfiguration().gpt;

    /* eslint-disable prefer-arrow-callback */
    googletag.cmd.push(function applyGPTSettings() {
      const pubads = googletag.pubads();
      if (collapseEmptyDivs) pubads.collapseEmptyDivs(true);
      if (singleRequestMode) pubads.enableSingleRequest();
      if (centerAds) pubads.setCentering(true);
      if (disableInitialLoad) pubads.disableInitialLoad();
      if (restrictDataProcessing) pubads.setPrivacySettings({ restrictDataProcessing: true });
    });
    /* eslint-enable prefer-arrow-callback */
  }

  /**
   * @private
   */
  /* eslint-disable class-methods-use-this */
  _enableGPTServices() {
    const { googletag } = window;
    /* eslint-disable prefer-arrow-callback */
    window.googletag.cmd.push(function enableGPTServices() { googletag.enableServices(); });
    /* eslint-enable prefer-arrow-callback */
  }
  /* eslint-enable class-methods-use-this */

  /**
   * @private
   */
  _handleSlotRenderEnded(e) {
    const isOutOfPage = e.slot.getOutOfPage();
    let dfpSlot;

    if (!isOutOfPage) {
      dfpSlot = Array.from(this.slots.values()).find(slot => slot.DFPSlot === e.slot);
    } else {
      dfpSlot = Object.values(this.outOfPageSlots).find(slot => slot.DFPSlot === e.slot);
    }

    if (dfpSlot) {
      dfpSlot.handleSlotRenderEnded(e);
      this._updateSlotMeta(dfpSlot);

      this.dispatchEvent('slotCreativeLoaded', { slot: dfpSlot });
    }

    this.dispatchEvent('slotRenderEnded', { originalEvent: e });
  }

  /**
   * @private
   */
  _handleSlotVisibilityChanged(e) {
    /* eslint-disable no-unused-vars */
    const dfpSlot = Array.from(this.slots.values()).find(slot => slot.DFPSlot === e.slot);
    /* eslint-enable no-unused-vars */
    if (dfpSlot) dfpSlot.handleSlotVisibilityChanged(e);

    this.dispatchEvent('slotVisibilityChanged', { originalEvent: e });
  }

  /**
   * @private
   */
  _handleImpressionViewable(e) {
    /* eslint-disable no-unused-vars */
    const dfpSlot = Array.from(this.slots.values()).find(slot => slot.DFPSlot === e.slot);
    /* eslint-enable no-unused-vars */
    if (dfpSlot) dfpSlot.handleImpressionViewable(e);

    this.dispatchEvent('impressionViewable', { originalEvent: e });
  }

  /**
   * @private
   */
  _bindGPTListeners() {
    const { googletag } = window;
    const {
      _handleSlotRenderEnded,
      _handleSlotVisibilityChanged,
      _handleImpressionViewable,
    } = this;
    /* eslint-disable prefer-arrow-callback */
    googletag.cmd.push(function bindGPTListeners() {
      const pubads = googletag.pubads();
      pubads.addEventListener('slotRenderEnded', _handleSlotRenderEnded);
      pubads.addEventListener('slotVisibilityChanged', _handleSlotVisibilityChanged);
      pubads.addEventListener('impressionViewable', _handleImpressionViewable);
    });
    /* eslint-enable prefer-arrow-callback */
  }

  /**
   * @private
   */
  _applyPageTargeting() {
    const { googletag } = window;
    const pageTargeting = this.getPageTargeting();

    // TODO: Unset unused keys in case this function is updating new targeting
    /* eslint-disable prefer-arrow-callback */
    googletag.cmd.push(function applyPageTargeting() {
      Object.keys(pageTargeting).forEach((key) => {
        const value = pageTargeting[key];
        if ((value !== undefined) && value.length) {
          googletag.pubads().setTargeting(key, value);
        }
      });
    });
    /* eslint-enable prefer-arrow-callback */
  }

  /**
   * @private
   */
  _resolveSlotReference(slotReference) {
    if (slotReference instanceof DFPAdSlot) return slotReference;

    if (typeof slotReference === 'object' && slotReference.enabled === false) return null;

    const element =
      (typeof slotReference === 'string') ? document.querySelector(slotReference) : slotReference;

    // Omitted the instanceof check because for some reason it's sometimes returning false when
    // the element IS a valid DOM element.
    // if (element instanceof Element) {
    //  return this.slots.get(element);
    // }
    const slot = this.slots.get(element);
    if (slot) return slot;

    return null;
    // throw new Error('MCPxxx: Invalid ad slot reference.');
  }

  /**
   * Handles the actual slot defining functionality
   * @private
   */
  _defineAdSlot(slotType, element) {
    return new Promise((resolve) => {
      const { googletag } = window;
      const { slots, _breakpoints, _updateSlotMeta } = this;
      const configuration = this.Configurator.getConfiguration();
      const { slotDefinitions } = configuration;
      const { slotMaxHeight, slotMaxWidth, slotMaxSize } = element.dataset;
      const breakpoint = getBreakpointSize(window, _breakpoints);

      let maxSize = [0, 0];

      function createEmptySlot() {
        const slot = { enabled: false };
        slots.set(element, slot);
        resolve(slot);
      }

      if (this.slots.get(element)) {
        consolv.log('MCP: Slot already defined', element);
        resolve();
        return;
      }

      const slotDefinition = slotDefinitions[slotType];

      // Bail if the slot is explicitly disabled
      if (!slotDefinition || slotDefinition.enabled === false) {
        createEmptySlot();
        return;
      }

      if (!slotDefinition.sizes || !slotDefinition.sizes.default) {
        consolv.log(`MCPxxx: Slot type "${slotType}" not configured.`);
        createEmptySlot();
        return;
      }

      // Limit slot sizes if explicitly set on DOM element
      if (typeof slotMaxSize !== 'undefined') {
        maxSize = slotMaxSize.split(',').map(v => Number(v));
      }

      if (typeof slotMaxWidth !== 'undefined') maxSize[0] = Number(slotMaxWidth);
      if (typeof slotMaxHeight !== 'undefined') maxSize[1] = Number(slotMaxHeight);

      const slotSizes = { ...slotDefinition.sizes };

      if (maxSize[0] > 0 || maxSize[1] > 0) {
        if (slotSizes.default) slotSizes.default = filterSizes(slotSizes.default, maxSize);
        if (slotSizes.responsive) {
          const responsiveSizes = { ...slotSizes.responsive };
          Object.keys(responsiveSizes).forEach((sizeKey) => {
            responsiveSizes[sizeKey] = filterSizes(responsiveSizes[sizeKey], maxSize);
          });
          slotSizes.responsive = responsiveSizes;
        }
      }

      // TODO: Better way to do this? Weird structure
      const slotMeta = {
        ...slotDefinition,
        slotType,
        maxSize,
        sizes: slotSizes,
        breakpoint,
      };

      this.dispatchEvent('beforeDefineSlot', { meta: { slot: slotMeta } });

      const hierarchy = this.getHierarchy(slotMeta, configuration);

      const _MCP = this;

      /* eslint-disable prefer-arrow-callback */
      googletag.cmd.push(function defineAdSlot() {
        /* eslint-disable-next-line no-param-reassign */
        if (!element.id) element.id = uniqueId(slotType);
        const currentSlot =
          googletag.defineSlot(hierarchy, buildSizeMap(slotSizes.default), element.id);

        // TODO: currentSlot empty sometimes?

        // if (!currentSlot) throw new Error('MCPxxx: Error creating new slot with DFP library.');
        if (!currentSlot) {
          consolv.log('MCP: Problem creating ad slot');
          resolve();
          return;
        }

        // Builds out a size map that is responive to the breakpoints set when the site initializes
        // the library
        if (_breakpoints && slotSizes.responsive) {
          const responsiveMap = buildResponsiveSizeMap(_breakpoints, slotSizes.responsive);
          currentSlot.defineSizeMapping(responsiveMap);
        }

        currentSlot.setTargeting('tagname', element.id);
        currentSlot.addService(googletag.pubads());

        const slot =
          new DFPAdSlot({ MCP: _MCP, slot: currentSlot, element, type: slotType, meta: slotMeta });
        _updateSlotMeta(slot);
        slots.set(element, slot);
        resolve(slot);
      });
      /* eslint-enable prefer-arrow-callback */
    });
  }

  /**
   * @private
   */
  _defineOutOfPageAdSlot(slotType, identifier) {
    return new Promise((resolve) => {
      const _MCP = this;
      const { googletag } = window;
      const { outOfPageSlots, _updateSlotMeta } = this;
      const configuration = this.Configurator.getConfiguration();
      const { slotDefinitions } = configuration;

      const slotMeta = {
        slotType,
        isOutOfPage: true,
      };

      const hierarchy = this.getHierarchy(slotMeta, configuration);

      /* eslint-disable prefer-arrow-callback */
      googletag.cmd.push(function defineOutOfPageAdSlot() {
        /* eslint-disable-next-line no-param-reassign */

        const currentSlot =
          googletag.defineOutOfPageSlot(hierarchy, slotType);

        // if (!currentSlot) throw new Error('MCPxxx: Error creating new slot with DFP library.');
        if (!currentSlot) {
          consolv.log('MCP: Problem creating ad slot');
          return;
        }

        currentSlot.addService(googletag.pubads());

        const slot =
          new DFPOutOfPageAdSlot({ MCP: _MCP, slot: currentSlot, type: slotType, meta: slotMeta });
        _updateSlotMeta(slot);
        outOfPageSlots[identifier] = slot;
        resolve(slot);
      });
      /* eslint-enable prefer-arrow-callback */
    });
  }

  /**
   * Updates meta data on specified slot
   * @private
   */
  _updateSlotMeta(slot) {
    const slotData = { slot: slot.toPlainObject() };
    const meta = this.Configurator.getConfiguration({ meta: slotData });

    Object.keys(meta).forEach((key) => {
      slot.setMetaData(key, meta[key]);
    });
  }

  /**
   * Iterates over all defined slots and destroys and prunes any slots whose elements are no
   * longer attached to the DOM.
   */
  pruneOrphanSlots() {
    this.slots.forEach((slot, element) => {
      if (!window.document.body.contains(element)) {
        if (slot && slot.destroy) slot.destroy();
        this.slots.delete(element);
        consolv.log('MCP: Pruned orphan slot', element);
      }
    });
  }

  /**
   * Returns the current page's hierarchy or site section
   * @return {Array<string>}
   */
  getHierarchy(slotMeta, configuration) {
    const { meta: otherMeta } = this._sourceOfTruth;

    const meta = {
      ...otherMeta,
      slot: slotMeta,
      environmentOverride: this._environmentOverride,
    };

    return evaluateFn(this._hierarchyFn, meta, configuration);
  }

  /**
   * Returns the current page's targeting parameters
   * @return {SlotTargeting}
   */
  getPageTargeting() {
    const { targetingWhitelist = [] } = this.Configurator.getConfiguration();
    const { meta } = this._sourceOfTruth;
    return getPageTargeting(targetingWhitelist, meta);
  }

  /**
   * Sets targeting for the page
   */
  /* eslint-disable prefer-arrow-callback,class-methods-use-this */
  setPageTargeting(pageTargeting) {
    window.googletag.cmd.push(function applyPageTargeting() {
      Object.keys(pageTargeting).forEach((key) => {
        const value = pageTargeting[key];
        if ((value !== undefined) && value.length) {
          window.googletag.pubads().setTargeting(key, value);
        }
      });
    });
  }
  /* eslint-enable prefer-arrow-callback,class-methods-use-this */

  /**
   * Appends an ad slot to the page after the initial page load
   * @param {SlotReference} element - DOM node for which to append the slot
   * @param {Object} [options] - Options that can be set on the appended slot
   * @param {string} [options.type] - The type of slot to append
   * @param {SlotTargeting} [options.additionalTargeting={}] - Additional targeting to add to the
   * slot refresh
   * @param {boolean} [options.load=true] - Allow the slot to load automatically
   * @return {Promise<DFPAdSlot, Error>}
   */
  appendSlot(element, {
    type,
    additionalTargeting = {},
    lazyLoad = false,
    immediatelyLoad = true,
  }) {
    return this.ready.then(() => {
      if (this.Configurator.getConfiguration().autoPruneOrphans) this.pruneOrphanSlots();

      const elementRef = (typeof element === 'string') ? document.getElementById(element) : element;
      if (!elementRef) return new Error('MCPxxx: Invalid element reference');

      return this._defineAdSlot(type, elementRef).then((slot) => {
        if (!slot) return null;

        const beforeEventParams =
          this.dispatchEvent('beforeAppendSlot', {
            immediatelyLoad,
            slots: new Map([[elementRef, slot]]),
          });

        if (beforeEventParams.defaultPrevented) return slot;

        // Wait for any promises that have been added by the event listeners
        return Promise.resolve(beforeEventParams.asyncEvents).then(() => {
          Object.entries(additionalTargeting).forEach(([key, value]) => {
            slot.setTargeting(key, value);
          });

          if (immediatelyLoad === true) slot.refresh();
          // this._updateSlotMeta(slot);
          consolv.log('MCP: Slot Appended', slot);
          this.dispatchEvent('slotCreated', { slot, initialRequest: false });

          return slot;
        });
      });
    });
  }

  /**
   * Refreshes an ad slot by finding slot through various types of references
   * @param {SlotReference} slotReference - Reference to the target slot
   * the refresh.
   * @return Promise<DFPAdSlot, Error>
   */
  refreshSlot(slotReference) {
    return this.refreshSlots([slotReference]);
  }

  /**
   * Refreshes multiple slots with a single ad request to allow for companion ads to be loaded
   * @param {Array<SlotReference>} slotReferences - List of slot references to refresh
   * @return Promise<DFPAdSlot, Error>
   */
  refreshSlots(slotsReference) {
    // TODO: Add additionalTargeting functionality
    return new Promise((resolve) => {
      try {
        const slots = slotsReference.reduce((acc, slotReference) => {
          const slot = this._resolveSlotReference(slotReference);
          if (slot) {
            // Allows slots to be configured to make isolated refreshes
            if (slot.meta.separateRefreshEvents && slotsReference.length > 1) {
              this.refreshSlots([slot]);
              return acc;
            }

            acc.set(slot.element, slot);
          }
          // else if (!(slot instanceof DFPAdSlot)) {
          //   consolv.error('MCP: Not a valid slot reference', slotReference);
          // }
          return acc;
        }, new Map());

        const beforeEventParams = this.dispatchEvent('beforeRefreshSlots', { slots });

        if (beforeEventParams.defaultPrevented) {
          resolve(null);
          return;
        }

        // Wait for any promises that have been added by the event listeners
        Promise.resolve(beforeEventParams.asyncEvents).then(() => {
          const nativeSlots = Array.from(slots).map(([_, slot]) => slot._DFPSlot);
          window.googletag.pubads().refresh(nativeSlots);
          slots.forEach((slot) => { slot.resetSlot(); });
          consolv.log('MCP: Refresh Slots', slots);

          this.dispatchEvent('afterRefreshSlots', { slots });
          resolve(true);
        });
      } catch (e) {
        consolv.error(e);
        resolve(null);
      }
    });
  }

  /**
   * Returns an ad slot object from a query selector or Element reference.
   * @param {ElementReference} elementReference - Reference to an ad slot
   * @return {DFPAdSlot}
   */
  getSlot(elementReference) {
    return this._resolveSlotReference(elementReference);
  }

  /**
   * Starts the initialization of the ad request
   * @param {object} [options] - List of options related to the ad library
   * @param {SlotTargeting} [options.additionalTargeting] - Additional targeting to apply to all
   *  slots. Returns a promise that resolves once the ad library has fully initialized.
   * @param {string} [options.selectedNetwork] - Optionally force the library to use a specific
   *  network
   * @param {SizeMapping} [options.breakpoints] - Optional list of page breakpoints
   * @return {Promise<this>}
   */
  initialize({ additionalTargeting, breakpoints, overlaySelectors, dfpEnv } = {}) {
    // Wait for setup to be ready
    return this.loaded
      .then(() => {
        this.additionalTargeting = additionalTargeting;
        this._breakpoints = breakpoints;
      })
      // Refresh sourceOfTruth in case any metadata values have changed since it has loaded
      .then(() => this._sourceOfTruth.refresh())
      .then(() => {
        // Remove after environment is passed in mdManager. stage is for food.com
        if (dfpEnv === 'staging' || dfpEnv === 'stage') this._environmentOverride = 'golden';

        const defaultConfiguration = {
          overlaySelectors: Array.isArray(overlaySelectors) ? overlaySelectors : [overlaySelectors],
        };

        const configuration = this.Configurator.getConfiguration(defaultConfiguration);
        this._overlaySelectors = configuration.overlaySelectors || [];

        this._applyGPTSettings();
        this._bindGPTListeners();
        this._applyPageTargeting();
        this._enableGPTServices();

        Promise.resolve(this.dispatchEvent('initialized').asyncEvents).then(() => {
          if (!this.isReady) {
            this._resolveReady(this);
            this._isReady = true;
            this.dispatchEvent('ready');
          }
        });

        return this;
      });
  }

  /**
   * Signifies that content on the page has changed, old slots should be pruned, and we should look
   * for new slots
   * @param {Element} [parentElement] - Reference to a container element containing content that
   *  has changed
   * @param {boolean} [shouldUpdateCorrelator] - Whether to consider this a new page. Useful for
   * SPA and infinite scroll pages
   */
  update(parentElement = null, shouldUpdateCorrelator = false) {
    const { googletag } = window;

    if (!this.isReady) return this.ready;

    consolv.log('MCP: update called');

    return this._sourceOfTruth.refresh().then(() => {
      if (shouldUpdateCorrelator) googletag.pubads().updateCorrelator();
      this.pruneOrphanSlots();
      return this.dispatchEvent('contentUpdated', { parentElement }).asyncEvents;
    });
  }

  /* eslint-disable-next-line class-methods-use-this */
  get version() {
    return buildEnv.version;
  }
}

export default MCP;
